export function mat3Identity() {
  return [1, 0, 0, 0, 1, 0, 0, 0, 1];
}

export function mat3Mul(a, b) {
  const o = new Array(9);
  for (let r = 0; r < 3; r++) {
    for (let c = 0; c < 3; c++) {
      o[r * 3 + c] = a[r * 3 + 0] * b[0 * 3 + c] + a[r * 3 + 1] * b[1 * 3 + c] + a[r * 3 + 2] * b[2 * 3 + c];
    }
  }
  return o;
}

export function mat3Inv(m) {
  const a = m[0], b = m[1], c = m[2];
  const d = m[3], e = m[4], f = m[5];
  const g = m[6], h = m[7], i = m[8];
  const A = e * i - f * h;
  const B = -(d * i - f * g);
  const C = d * h - e * g;
  const D = -(b * i - c * h);
  const E = a * i - c * g;
  const F = -(a * h - b * g);
  const G = b * f - c * e;
  const H = -(a * f - c * d);
  const I = a * e - b * d;
  const det = a * A + b * B + c * C;
  if (Math.abs(det) < 1e-12) return null;
  const s = 1 / det;
  return [A * s, D * s, G * s, B * s, E * s, H * s, C * s, F * s, I * s];
}

export function scaleInsideMatrix(size, scale) {
  const f = scale;
  const w = size;
  const t = (w - w * f) / 2;
  return [f, 0, t, 0, f, t, 0, 0, 1];
}

export function photoPadMatrix(photoWidth, photoHeight, canvasSize = 1200) {
  const w = photoWidth;
  const h = photoHeight;
  const sq = Math.max(w, h);
  const padX = (sq - w) / 2;
  const padY = (sq - h) / 2;
  const s = canvasSize / sq;
  return [s, 0, s * padX, 0, s, s * padY, 0, 0, 1];
}

export function photoToRenderNative(align, canvasSize = 1200, photoWidth, photoHeight) {
  const Hr2p = flat3(align.homography);
  const matchScale = align.scale ?? 1;
  const MPhoto = photoPadMatrix(photoWidth, photoHeight, canvasSize);
  const Hp2rScaled = mat3Inv(Hr2p);
  if (!Hp2rScaled) return null;
  const MUnscale = mat3Inv(scaleInsideMatrix(canvasSize, matchScale));
  return mat3Mul(mat3Mul(MUnscale, Hp2rScaled), MPhoto);
}

export function photoToFrame(HrenderToFrame, HphotoToRenderNative) {
  if (!HphotoToRenderNative) return null;
  return mat3Mul(flat3(HrenderToFrame), HphotoToRenderNative);
}

export function flat3(H) {
  if (Array.isArray(H) && H.length === 3 && Array.isArray(H[0])) {
    return [H[0][0], H[0][1], H[0][2], H[1][0], H[1][1], H[1][2], H[2][0], H[2][1], H[2][2]];
  }
  return H;
}

export function projectPoints(Hflat, pts) {
  const out = [];
  for (const [x, y] of pts) {
    const xp = Hflat[0] * x + Hflat[1] * y + Hflat[2];
    const yp = Hflat[3] * x + Hflat[4] * y + Hflat[5];
    const w = Hflat[6] * x + Hflat[7] * y + Hflat[8];
    out.push([xp / w, yp / w]);
  }
  return out;
}

export function segmentationPolygons(ann) {
  const seg = ann.segmentation;
  if (!seg || !seg.length) {
    const [x, y, w, h] = ann.bbox || [0, 0, 0, 0];
    return [[[x, y], [x + w, y], [x + w, y + h], [x, y + h]]];
  }
  return seg.filter((s) => s && s.length >= 6).map((s) => {
    const pts = [];
    for (let i = 0; i < s.length; i += 2) pts.push([s[i], s[i + 1]]);
    return pts;
  });
}

export function categoryColor(categoryId) {
  const palette = [
    "#ffff00", "#00b4ff", "#00ff00", "#ffff00",
    "#ff0000", "#ff00ff", "#0080ff", "#80ff00",
    "#ff8000", "#b400ff", "#00ffb4", "#ffb400",
  ];
  return palette[categoryId % palette.length];
}


/** EMA blend of two 3x3 homographies (t=0 keeps A, t=1 takes B). */
export function blendHomography(Ha, Hb, t) {
  const a = flat3(Ha);
  const b = flat3(Hb);
  const wa = a[8] || 1;
  const wb = b[8] || 1;
  const o = new Array(9);
  for (let i = 0; i < 9; i++) {
    o[i] = (1 - t) * (a[i] / wa) + t * (b[i] / wb);
  }
  const w = o[8] || 1;
  return o.map((v) => v / w);
}

/** Max corner displacement (px) when applying Hb vs Ha on a quad in render space. */
export function homographyCornerDelta(Ha, Hb, corners) {
  const pa = projectPoints(flat3(Ha), corners);
  const pb = projectPoints(flat3(Hb), corners);
  let maxD = 0;
  for (let i = 0; i < pa.length; i++) {
    maxD = Math.max(maxD, Math.hypot(pb[i][0] - pa[i][0], pb[i][1] - pa[i][1]));
  }
  return maxD;
}
